import React, { Component } from 'react'
import { Provider } from 'react-redux'
import { ConnectedRouter } from 'connected-react-router'
import { persistStore } from 'redux-persist'
import store, { history } from 'src/app/store'
import ErrorBoundary from 'src/components/ErrorBoundary'
import { Loader } from 'src/components/Loader'
import networkService from 'src/app/network'
import { getPlain } from 'src/utils/request-client'
import { ChakraProvider } from '@chakra-ui/react'
import {
  logError,
  getLogLevel,
  hasLocalStorage,
  getBrandSettings,
  getFeatures,
  isEmpty,
  getErrorMessage,
  setThemeColors,
  isNodeLocal,
} from 'src/utils/helpers'
import BrokerService from 'src/utils/broker-service'
import LanguageWrapper from 'src/components/LanguageWrapper'
import { setSettings, setLanguage, clearAppCache, setFeatures } from './actions'
// For widget
import { WidgetContext, widgetContextSettings } from 'src/modules/App/WidgetContext'
// MemoryRouter is used for "history" context in widget iframe
import { MemoryRouter } from 'react-router'
import AppLayout from './AppLayout.container'
import { getBrandingCssUri } from 'src/utils/theme'
import 'src/fonts/index.css'
import './App.less'
import { theme } from 'src/theme/theme'

import { library, config, dom } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/pro-solid-svg-icons'
import { far } from '@fortawesome/pro-regular-svg-icons'
import { fal } from '@fortawesome/pro-light-svg-icons'
import { fad } from '@fortawesome/pro-duotone-svg-icons'
import withToast from 'src/components/Toast'
import Accessibe from 'src/components/Accessibe'

// Prevent FontAwesome to add the their styles automatically because of the Widget.
// Instead, get the styles and add them manually where it's needed. See addFontAwesomeBasicStyles method.
config.autoAddCss = false
const faDomCSS = dom.css()
library.add(far, fas, fal, fad)

class App extends Component {
  state = {
    rehydrate: false,
    colors: {},
  }

  componentDidMount() {
    // Use redux persist store only if localStorage (cookies) are enabled
    if (hasLocalStorage()) {
      // TODO: Check if we should use different storage engine:
      // https://github.com/rt2zz/redux-persist/tree/v4.6.0#storage-engines
      persistStore(
        store,
        {
          // List of reducers not to persist in storage (also set in src/app/offline-config.js)
          blacklist: ['nonPersistentAppReducer'],
        },
        this.initialize,
      )
    } else {
      this.initialize()
    }

    networkService.setupInterceptors(store)
    if (this.props.isWidget) {
      const toastPortal = document.getElementById('chakra-toast-portal')
      const root = document.getElementById('kiosk-container')
      root.contentWindow.document.body.appendChild(toastPortal)
    }

    // Set to which DOM element would global notifications be attached
    // notification.config({
    //   getContainer: () => getBodyContainer()
    // })
  }

  shouldComponentUpdate(nextProps) {
    const { isWidget, isWidgetFullSize } = this.props

    // Re-render if widget and "full size" state changes
    if (isWidget && nextProps.isWidgetFullSize !== isWidgetFullSize) {
      return true
    }

    const { nonPersistentAppReducer } = store.getState()

    // Check if app is rendered based on global redux state
    // It's global so we can re-render whole app if needed (e.g. language change)
    const isAppRendered =
      nonPersistentAppReducer.hasOwnProperty('isAppRendered') &&
      nonPersistentAppReducer.isAppRendered === true

    // If it's not rendered it should update (re-render)
    return !isAppRendered
  }

  initialize = async () => {
    try {
      const { isWidget, widgetLanguage, broker, toast, frameDocument } = this.props

      if (isWidget) {
        // Set broker if passed from widget script
        if (broker) {
          BrokerService.setBroker(broker)
        }

        // Set language if passed from widget script to global redux store
        if (widgetLanguage) {
          try {
            // Setting language with
            store.dispatch(setLanguage(widgetLanguage, false, 'widget'))
          } catch (e) {
            logError('Dispatch set language', e)
          }
        }
      }

      this.addFontAwesomeBasicStyles(isWidget, frameDocument)

      await this.handleCoreSettings()

      const settings = await this.handleSettings()
      await this.handleFeatures()

      if (isEmpty(settings)) {
        return false
      }

      if (typeof settings === 'object' && settings.hasOwnProperty('error')) {
        toast({
          title: 'Error',
          description: getErrorMessage(settings.error),
          status: 'error',
          duration: null,
        })

        return false
      }

      if (!isWidget) {
        this.applyAppName(settings)
      }

      this.setState({
        rehydrate: true,
      })

      return true
    } catch (e) {
      logError('On app initialize', e)
      return false
    }
  }

  /**
   * Add/apply FontAwesome basic styles to document head
   *
   * On App mount add FA styles to Widget iframe head for Widget and in parent/main window doc head when not widget.
   *
   * @param {Boolean} isWidget Widget flag (is widget or not)
   * @param {Object} frameDocument Widget iframe document or undefined
   */
  addFontAwesomeBasicStyles = (isWidget, frameDocument) => {
    const doc = isWidget ? frameDocument : document
    const head = doc.head || doc.getElementsByTagName('head')[0]

    const styleElement = document.createElement('style')
    styleElement.setAttribute('type', 'text/css')
    styleElement.appendChild(document.createTextNode(faDomCSS))

    head.appendChild(styleElement)
  }

  /**
   * Get only core settings, to set favicon and splash screen as quickly as possible.
   *
   * @return {boolean}
   */
  handleCoreSettings = async () => {
    try {
      const { isWidget } = this.props

      // Not needed for widget
      if (isWidget) {
        return false
      }

      const coreSettings = await getBrandSettings('c')
      if (!(coreSettings && coreSettings.hasOwnProperty('graphics'))) {
        return false
      }

      this.applyIcons(coreSettings.graphics)
      this.applySplashScreens(coreSettings.graphics)

      return true
    } catch (e) {
      logError('Handle core settings', e)
    }
  }

  handleFeatures = async () => {
    try {
      // Get all features
      const data = await getFeatures()

      // Set broker features to global redux store
      store.dispatch(setFeatures(data))

      return data
    } catch (e) {
      logError('Handle features', e)
    }
  }

  /**
   * Get settings from backend and apply then to global redux store.
   *
   * @return {object}
   */
  handleSettings = async () => {
    try {
      const { isWidget } = this.props

      // Currently backend returns same settings for these two initial referrers
      const settingsReferer = isWidget ? 'i-w' : 'i-a'

      // Get all settings, first widget settings were just for colors, when using: getBrandSettings ('w')
      const settings = await getBrandSettings(settingsReferer)
      if (typeof settings !== 'object' || isEmpty(settings) || settings.hasOwnProperty('error')) {
        return settings
      }

      if (
        settings.hasOwnProperty('brand') &&
        settings.brand &&
        settings.brand.hasOwnProperty('colors')
      ) {
        await this.applyBrandColors(settings.brand.colors).catch(e => {
          logError('Apply brand colors', e, getLogLevel(e))
        })
      }

      // Set broker settings to global redux store
      store.dispatch(setSettings(settings))

      return settings
    } catch (e) {
      logError('Handle settings', e)
    }
  }

  applyBrandColors = async colors => {
    this.setState({
      colors: colors,
    })

    const response = await getPlain(getBrandingCssUri(colors))

    if (!isEmpty(response) && response.hasOwnProperty('css')) {
      setThemeColors(colors)
      const style = document.createElement('style')
      style.id = 'kiosk-branding-color'
      style.appendChild(document.createTextNode(response.css))
      document.body.appendChild(style)
    }

    const themeColorEl = document.querySelector('#theme-color')
    if (themeColorEl) {
      themeColorEl.setAttribute('content', colors.brandColor)
    }
    return Promise.resolve()
  }

  applyAppName = settings => {
    try {
      if (
        !settings.hasOwnProperty('brand') ||
        !settings.brand.hasOwnProperty('name') ||
        isEmpty(settings.brand.name)
      ) {
        return false
      }

      document
        .getElementById('apple-mobile-web-app-title')
        .setAttribute('content', settings.brand.name)
    } catch (e) {
      logError('Could not apply app name', e)
    }
  }

  applyIcons = graphics => {
    try {
      if (isEmpty(graphics) || !graphics.hasOwnProperty('icons') || isEmpty(graphics.icons)) {
        return false
      }

      const iconsToAppendToDom = ['favicon', 'apple-touch-icon']
      graphics.icons.forEach(icon => {
        if (iconsToAppendToDom.includes(icon.name)) {
          document.getElementById(icon.name).setAttribute('href', icon.src)
        }
      })
    } catch (e) {
      logError('Could not apply favicon', e)
    }
  }

  applySplashScreens = graphics => {
    try {
      if (isEmpty(graphics) || !graphics.hasOwnProperty('screen') || isEmpty(graphics.screen)) {
        return false
      }

      graphics.screens.forEach(screen => {
        document.getElementById(screen.name).setAttribute('href', screen.src)
      })
    } catch (e) {
      logError('Could not apply splash screens', e)
    }
  }

  render() {
    const {
      broker,
      isWidget,
      isWidgetActive,
      isWidgetFullSize,
      handleCloseWidget,
      initialWidgetFeature,
      frameDocument,
      frameWindow,
    } = this.props
    const { rehydrate } = this.state

    if (!rehydrate) {
      return <Loader spinnerStyle={{ width: '3rem', height: '3rem' }} />
    }

    const getWidgetContext = {
      ...widgetContextSettings,
      isWidget: !!isWidget,
      isWidgetActive: !!isWidgetActive,
      isWidgetFullSize: !!isWidgetFullSize,
      handleCloseWidget: handleCloseWidget,
      initialWidgetFeature: initialWidgetFeature,
      frameDocument: frameDocument,
      frameWindow: frameWindow,
    }

    const OptionalWrapper = ({ condition, wrap, children }) =>
      condition ? wrap(children) : children

    return (
      <Provider store={store}>
        {!isNodeLocal() && <Accessibe />}
        <LanguageWrapper>
          <ConnectedRouter history={history}>
            <ChakraProvider theme={theme}>
              <ErrorBoundary clearAppCache={() => store.dispatch(clearAppCache())}>
                <div id='kiosk-body-container' className='kiosk-body-container' />
                <WidgetContext.Provider value={getWidgetContext}>
                  <OptionalWrapper
                    condition={isWidget}
                    wrap={children => <MemoryRouter>{children}</MemoryRouter>}
                  >
                    <AppLayout
                      broker={broker}
                      isWidget={!!isWidget}
                      isWidgetActive={!!isWidgetActive}
                      isWidgetFullSize={!!isWidgetFullSize}
                      handleCloseWidget={handleCloseWidget}
                    />
                  </OptionalWrapper>
                </WidgetContext.Provider>
              </ErrorBoundary>
            </ChakraProvider>
          </ConnectedRouter>
        </LanguageWrapper>
      </Provider>
    )
  }
}

export default withToast(App)
